

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { API_URL } from "../config";

/*
=========================================
PREVIOUS TAB-CAPTURE IMPLEMENTATION (html2canvas)
=========================================
// import html2canvas from "html2canvas";
// 
// function ActivityTracker() {
//   const { user } = useAuth();
//   const location = useLocation();
// 
//   useEffect(() => {
//     if (!user || !user._id) return;
//     const isEmployeeRole = user.role === "employee";
//     const isTraineeOrIntern = user.userType === "trainee" || user.userType === "intern" || user.role === "trainee" || user.role === "intern";
//     if (!isEmployeeRole && !isTraineeOrIntern) return;
// 
//     const captureAndUpload = async () => {
//       try {
//         if (document.visibilityState !== "visible") return;
//         const canvas = await html2canvas(document.body, { useCORS: true, logging: false, allowTaint: true, scale: 0.8 });
//         const base64Image = canvas.toDataURL("image/jpeg", 0.6);
//         await axios.post(`${API_URL}/api/screenshots/upload`, {
//           employeeId: user._id,
//           imageBuffer: base64Image,
//           currentRoute: location.pathname
//         });
//       } catch (error) {
//         console.error("[ActivityTracker] Capturing or uploading failed:", error);
//       }
//     };
//     const initialTimeout = setTimeout(captureAndUpload, 5000);
//     const interval = setInterval(captureAndUpload, 300000);
//     return () => {
//       clearTimeout(initialTimeout);
//       clearInterval(interval);
//     };
//   }, [user, location.pathname]);
//   return null;
// }
// export default ActivityTracker;
=========================================
*/

function ActivityTracker() {
  const { user, workingHours, setWorkingHours, isTracking, setIsTracking, breakStatus, isOnBreak, startBreak, resumeBreak } = useAuth();
  const location = useLocation();
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    const wasTracking = localStorage.getItem("trackingActive");

    if (wasTracking === "true") {
      setError("Page was refreshed. Please click 'Start Screen Tracker' again.");
    }
  }, []);

  const captureIntervalRef = useRef(null);
  const locationRef = useRef(location.pathname);
  const lastCaptureTimeRef = useRef(null);

  // Sync tracking status to global context
  useEffect(() => {
    setIsTracking(!!stream);
  }, [stream, setIsTracking]);

  // Sync route path changes so screenshots are uploaded with correct route context
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  // Determine if user is subject to tracking
  const isTrackedUser = (() => {
    if (!user || !user._id) return false;
    const isEmployeeRole = user.role === "employee";
    const isTraineeOrIntern =
      user.userType === "trainee" ||
      user.userType === "intern" ||
      user.role === "trainee" ||
      user.role === "intern";
    return isEmployeeRole || isTraineeOrIntern;
  })();

  // Fetch initial working hours from the backend
  useEffect(() => {
    if (!isTrackedUser || !user?._id) return;

    const fetchHours = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/today`, {
          params: { employeeId: user._id }
        });
        if (res.data && res.data.workingHours !== undefined) {
          setWorkingHours(res.data.workingHours);
        }
      } catch (err) {
        console.error("[ActivityTracker] Failed to fetch initial hours:", err);
      }
    };

    fetchHours();
  }, [user, isTrackedUser, setWorkingHours]);

  // Format hours for display (HH:mm:ss)
  const formatTimeDisplay = (decimalHours) => {
    const totalSeconds = Math.floor(decimalHours * 3600);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Core capture and upload function
  const captureAndUploadFrame = async (activeStream) => {
    if (isOnBreak) return;
    if (!activeStream || !activeStream.active) return;
    try {
      const videoTrack = activeStream.getVideoTracks()[0];
      if (!videoTrack || videoTrack.readyState !== "live") return;

      // Create an offscreen video element to receive the stream frame
      const video = document.createElement("video");
      video.srcObject = activeStream;
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        video.play();
        // Give the video a moment to buffer the current frame
        setTimeout(() => {
          // Scale down image to a max width of 1280px to optimize size
          const maxDimension = 1280;
          let width = video.videoWidth || 1280;
          let height = video.videoHeight || 720;

          if (width > maxDimension) {
            const ratio = maxDimension / width;
            width = maxDimension;
            height = Math.round(height * ratio);
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, width, height);

          const base64Image = canvas.toDataURL("image/jpeg", 0.6);

          const now = Date.now();
          let elapsedSec = 0;
          if (lastCaptureTimeRef.current) {
            elapsedSec = (now - lastCaptureTimeRef.current) / 1000;
          }
          lastCaptureTimeRef.current = now;

          axios
            .post(`${API_URL}/api/screenshots/upload`, {
              employeeId: user._id,
              imageBuffer: base64Image,
              currentRoute: locationRef.current,
              trackedSeconds: Math.max(0, elapsedSec),
            })
            .then(() => {
              console.log("[ActivityTracker] Full screen screenshot uploaded.");
            })
            .catch((err) => {
              console.error("[ActivityTracker] Screen upload error:", err);
            });

          // Cleanup video reference
          video.srcObject = null;
        }, 800);
      };
    } catch (err) {
      console.error("[ActivityTracker] Frame capture exception:", err);

    }
  };

  // Setup/Tear down intervals when stream status changes
  useEffect(() => {
    if (!isTrackedUser || !stream || isOnBreak) {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
      return;
    }

    // Capture initial frame after 3 seconds
    const initialTimeout = setTimeout(() => {
      captureAndUploadFrame(stream);
    }, 3000);

    // Use dynamic interval from user profile (default to 5 mins if not set)
    const intervalSeconds = user?.screenshotInterval || 300;
    const intervalMs = intervalSeconds * 1000;

    // Capture every dynamic interval
    const interval = setInterval(() => {
      captureAndUploadFrame(stream);
    }, intervalMs);

    captureIntervalRef.current = interval;

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      captureIntervalRef.current = null;
    };
  }, [stream, isTrackedUser, user?.screenshotInterval, isOnBreak]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
      }
    };
  }, []);

  const handleStartTracking = async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
        },
        audio: false,
      });

      setStream(mediaStream);
      lastCaptureTimeRef.current = Date.now();

      localStorage.setItem("trackingActive", "true");

      // Listen for stream ending (e.g. user clicks browser's native "Stop sharing")
      mediaStream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          localStorage.removeItem("trackingActive");

          setStream(null);
          setError(
            "Screen sharing was stopped. Please re-enable tracking to capture activity."
          );
        };
      });
    } catch (err) {
      console.error("[ActivityTracker] Failed to get display media:", err);
      setError("Screen sharing permission is required to continue work.");
    }
  };

  // If user is not an employee or trainee/intern, don't show or track anything
  if (!isTrackedUser) {
    return null;
  }

  // --- Beautiful glassmorphic UI styling ---
  const containerStyle = {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: 999999,
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
  };

  const cardStyle = {
    background: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)",
    width: "320px",
    padding: "20px",
    color: "#f8fafc",
  };

  const activePillStyle = {
    background: "rgba(16, 185, 129, 0.9)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(52, 211, 153, 0.3)",
    borderRadius: "30px",
    boxShadow: "0 4px 15px rgba(16, 185, 129, 0.2)",
    padding: "10px 20px",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  };

  const buttonStyle = {
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    width: "100%",
    cursor: "pointer",
    transition: "transform 0.1s ease, box-shadow 0.2s ease",
    marginTop: "12px",
    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
  };

  return (
    <div style={containerStyle}>
      {isOnBreak ? (
        <div style={{ ...cardStyle, background: "rgba(217, 119, 6, 0.95)", border: "1px solid rgba(255, 255, 255, 0.2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <span style={{ display: "inline-block", width: "10px", height: "10px", backgroundColor: "#fff", borderRadius: "50%", boxShadow: "0 0 10px #fff" }}></span>
            <strong style={{ fontSize: "15px", fontWeight: "700" }}>On Break</strong>
          </div>
          <p style={{ fontSize: "12px", color: "#fef3c7", margin: "0 0 12px 0", lineHeight: "1.5" }}>
            Your activity tracking is currently paused. Break time counts toward your shift hours.
          </p>
          <button
            style={{ ...buttonStyle, background: "#ffffff", color: "#b45309", marginTop: "4px" }}
            onClick={resumeBreak}
            onMouseDown={(e) => e.target.style.transform = "scale(0.98)"}
            onMouseUp={(e) => e.target.style.transform = "none"}
          >
            Resume Work
          </button>
        </div>
      ) : stream ? (
        isMinimized ? (
          <div style={activePillStyle} onClick={() => setIsMinimized(false)}>
            <span style={{ display: "inline-block", width: "10px", height: "10px", backgroundColor: "#10b981", borderRadius: "50%", boxShadow: "0 0 10px #10b981" }}></span>
            <span>{formatTimeDisplay(workingHours)}</span>
          </div>
        ) : (
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className="pulse-dot" style={{ display: "inline-block", width: "10px", height: "10px", backgroundColor: "#10b981", borderRadius: "50%", boxShadow: "0 0 10px #10b981" }}></span>
                <span style={{ fontSize: "14px", fontWeight: "600" }}>Tracking Active</span>
              </div>
              <button
                onClick={() => setIsMinimized(true)}
                style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: "12px" }}
              >
                Minimize
              </button>
            </div>

            <div style={{ textAlign: "center", margin: "10px 0 15px 0" }}>
              <div style={{ fontSize: "28px", fontWeight: "700", letterSpacing: "1px", color: "#10b981" }}>
                {formatTimeDisplay(workingHours)}
              </div>
              <div style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase" }}>Work Time Today</div>
            </div>

            <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: "1.5" }}>
              Screenshots are captured every {user?.screenshotInterval ?
                (user.screenshotInterval < 60 ? `${user.screenshotInterval}s` : `${Math.round(user.screenshotInterval / 60)}m`)
                : "5m"}. Working hours are tracked only while screen sharing is active.
            </p>
          </div>
        )
      ) : (
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <span style={{ color: "#f59e0b", fontSize: "18px" }}>⚠️</span>
            <strong style={{ fontSize: "15px", fontWeight: "700" }}>Tracker Required</strong>
          </div>
          <p style={{ fontSize: "12px", color: "#cbd5e1", margin: "0 0 8px 0", lineHeight: "1.5" }}>
            Please share your **Entire Screen** (all windows/apps) to record activity for your shift.
          </p>
          {error && (
            <p style={{ fontSize: "11px", color: "#f87171", margin: "4px 0 0 0", lineHeight: "1.4", fontWeight: "500" }}>
              {error}
            </p>
          )}
          <button
            style={buttonStyle}
            onClick={handleStartTracking}
            onMouseDown={(e) => e.target.style.transform = "scale(0.98)"}
            onMouseUp={(e) => e.target.style.transform = "none"}
          >
            Start Screen Tracker
          </button>
        </div>
      )}
    </div>
  );
}

export default ActivityTracker;
